import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
} from "@mui/material";
import { useContext, useState } from "react";
import { api } from "../../utils/axios-instance";
import { auth } from "../../helper/firebaseClient";
import toast, { Toaster } from "react-hot-toast";
import fileContext from "../../context/fileContext";
import useAuth from "../../stores/authStore";
import CloseIcon from "@mui/icons-material/Close";

export function ShowVersions({ file, openFile, closeMenu }) {
  const [isOpen, setIsOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const context = useContext(fileContext);
  const { updateFilesState, setFilteredData } = context;
  const CACHE_NAME = "blob-cache";
  const profileData = useAuth((state) => state.profileData);

  const restorePreviousVersion = async (id) => {
    setRestoringId(id);
    try {
      const token = await auth.currentUser.getIdToken();

      // Backend copies the snapshot object files/{org}/{id} onto the canonical
      // live object files/{org}/{file.id} and keeps curr_version = file.id.
      // No new version id is minted; the storage pointer never moves.
      await api.post(`file/restore-file-version/${file.id}/`, {
        idToken: token,
        srcVersion: id,
      });

      // The cached copy is now stale (it holds the pre-restore bytes). Bust it
      // so the reopen re-fetches the restored content from storage.
      await removeFileFromCache(token);

      // Reopen from the canonical path. curr_version is unchanged (= file.id),
      // so we open the file by its own id — NOT a freshly minted version id.
      openFile(file.id);
      setIsOpen(false);
      toast.success("Version restored");
    } catch (error) {
      console.error("Error restoring version:", error);
      toast.error("Failed to restore version");
    } finally {
      setRestoringId(null);
    }
  };

  const removeFileFromCache = async (token) => {
    try {
      const fileIdentifier = `${file.id}-${token}`;
      const cache = await caches.open(CACHE_NAME);
      const success = await cache.delete(fileIdentifier);

      if (success) {
        console.log("File removed from cache:", fileIdentifier);
      } else {
        console.log("File not found in cache:", fileIdentifier);
      }
    } catch (error) {
      console.error("Error while removing file from cache:", error);
    }
  };

  return (
    <div>
      <Toaster reverseOrder={false} position="bottom-left" />
      <button onClick={() => setIsOpen(true)}>Show Versions</button>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          style: {
            borderRadius: "15px",
            maxHeight: "78%",
            width: "50%",
            padding: "0px",
          },
        }}
        maxWidth="lg"
      >
        <DialogContent
          style={{
            backgroundColor: "white",
            position: "relative",
            padding: "0px",
          }}
        >
          <div className="flex flex-col">
            <div className="flex justify-between items-center p-4">
              <div>
                <h3 className="text-lg font-bold">File Versions</h3>
                <p className="text-base">Track your file versions here</p>
              </div>
              <div className="p-1 bg-[#F2786B] rounded-full hover:cursor-pointer">
                <CloseIcon
                  sx={{ color: "white" }}
                  onClick={() => {
                    setIsOpen(false);
                    closeMenu();
                  }}
                />
              </div>
            </div>
            <div
              style={{ borderBottom: "0.25px solid grey" }}
              className="w-full"
            ></div>
            {file?.versions?.map((version, index) => {
              return (
                <div
                  key={version.id}
                  className="flex items-center group relative pl-6 h-14"
                >
                  <p>{version.lastUpdate}</p>
                  <div
                    style={{ borderLeft: "0.25px solid grey" }}
                    className="h-full mx-10"
                  ></div>
                  <h2 className="text-lg italic font-semibold w-[20%]">{`Version ${index + 1}`}</h2>

                  <button
                    className="px-4 py-1 rounded-md border bg-[#1c4ed8] text-white absolute right-10 invisible group-hover:visible disabled:opacity-50"
                    onClick={() => restorePreviousVersion(version.id)}
                    disabled={restoringId !== null}
                  >
                    {restoringId === version.id ? "Restoring..." : "Restore"}
                  </button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}